// frontend/src/pages/employees/EmployeesPage.jsx
//
// The shell: tabs, period, URL state. It fetches nothing.
//
// Every panel below owns its own request and its own error, so one
// endpoint returning a 500 shows a message in that panel and leaves the
// rest of the page standing. The version this replaces downloaded the
// firm's entire time ledger here and summed it in JavaScript.
import React, { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import ExploreTab from "./ExploreTab";
import PeriodControl from "./PeriodControl";
import PersonView from "./PersonView";
import TeamTab from "./TeamTab";
import { anchorParam, GRANULARITIES, normalizeAnchor, periodBounds,
         usePeriod } from "./usePeriod";

const TABS = [
  { key: "mine", label: "My Time" },
  { key: "team", label: "Team", managerOnly: true },
  { key: "explore", label: "Explore", managerOnly: true },
];

const tabBar = {
  display: "flex", gap: 8, borderBottom: "2px solid #e5e7eb",
  marginBottom: 24,
};

function tabButton(active) {
  return {
    padding: "12px 24px", border: "none", borderRadius: "8px 8px 0 0",
    fontWeight: 600, cursor: "pointer", fontSize: 14,
    background: active ? "#3b82f6" : "transparent",
    color: active ? "white" : "#6b7280",
  };
}

export default function EmployeesPage({ user }) {
  const [params, setParams] = useSearchParams();

  const isManager = ["MANAGER", "ADMIN"].includes(
    String(user?.role).toUpperCase());

  const tabs = TABS.filter((t) => isManager || !t.managerOnly);

  // Every URL value falls back rather than erroring: a stale or
  // hand-edited URL must not blank the page.
  const rawTab = params.get("tab");
  const tab = tabs.some((t) => t.key === rawTab) ? rawTab : "mine";

  const rawGranularity = params.get("p");
  const granularity = GRANULARITIES.includes(rawGranularity)
    ? rawGranularity : "month";

  const anchor = normalizeAnchor(granularity, params.get("d"));

  // ?emp= is honoured only for a manager. The server scopes a staff user
  // regardless, so the risk here is cosmetic and worse for it: the
  // heading would name a person whose numbers are not on screen.
  const rawEmp = params.get("emp");
  const selectedEmp = isManager && rawEmp ? Number(rawEmp) : null;
  const selectedName = isManager ? params.get("empName") : null;

  const write = useCallback((next) => {
    const merged = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") merged.delete(k);
      else merged.set(k, String(v));
    });
    setParams(merged, { replace: true });
  }, [params, setParams]);

  const onPeriodChange = useCallback(({ granularity: g, anchor: a }) => {
    write({ p: g, d: anchorParam(g, a) });
  }, [write]);

  const period = usePeriod({ granularity, anchor, onChange: onPeriodChange });

  const onCustomRange = useCallback((fromISO, toISO) => {
    write({ p: "custom", d: anchorParam("custom", `${fromISO}..${toISO}`) });
  }, [write]);

  const bounds = useMemo(
    () => periodBounds(granularity, anchor), [granularity, anchor]);

  const selectEmployee = useCallback((id, name) => {
    write({ tab: "team", emp: id, empName: name });
  }, [write]);

  const backToTeam = useCallback(() => {
    write({ tab: "team", emp: null, empName: null });
  }, [write]);

  return (
    <div style={{ padding: 24, background: "#f9fafb", minHeight: "100vh" }}>
      <h2 style={{ margin: "0 0 24px" }}>
        {isManager ? "Employees" : "My Time"}
      </h2>

      {tabs.length > 1 && (
        <div style={tabBar} role="tablist">
          {tabs.map((t) => (
            <button
              key={t.key} type="button" role="tab"
              aria-selected={tab === t.key}
              onClick={() => write({ tab: t.key, emp: null, empName: null })}
              style={tabButton(tab === t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      <PeriodControl
        granularity={granularity}
        label={period.label}
        startDate={bounds.startDate}
        endDate={bounds.endDate}
        onGranularity={period.setGranularity}
        onStep={period.step}
        onCustomRange={onCustomRange}
      />

      {tab === "mine" && (
        <PersonView
          employeeId={user?.id}
          name={null}
          granularity={granularity}
          anchor={anchor}
          startDate={bounds.startDate}
          endDate={bounds.endDate}
        />
      )}

      {tab === "team" && !selectedEmp && (
        <TeamTab
          startDate={bounds.startDate}
          endDate={bounds.endDate}
          onSelectEmployee={selectEmployee}
        />
      )}

      {tab === "team" && selectedEmp && (
        <>
          <button
            type="button" onClick={backToTeam}
            style={{ background: "none", border: "none", color: "#3b82f6",
                     cursor: "pointer", fontSize: 13, fontWeight: 600,
                     padding: 0, marginBottom: 16 }}
          >
            ◀ Back to Team
          </button>
          <PersonView
            employeeId={selectedEmp}
            name={selectedName || "Employee"}
            granularity={granularity}
            anchor={anchor}
            startDate={bounds.startDate}
            endDate={bounds.endDate}
          />
        </>
      )}

      {tab === "explore" && (
        <ExploreTab
          filters={{
            startDate: bounds.startDate,
            endDate: bounds.endDate,
          }}
        />
      )}
    </div>
  );
}
